/**
 * Capture, from every React client (block 03).
 *
 * The webapp modal, the extension popup and — when mobile thaws — the composer
 * sheet all call this one hook. None of them passes a surface: that is
 * configured once per app in `createNookClient`, so no call site can get it
 * wrong and no nook can claim to come from somewhere it did not.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NookKind } from "@nook/schema";
import { getNookClient } from "../client.js";
import { PlanExpiredError } from "../http.js";
import { queryKeys } from "../query-keys.js";
/**
 * One `Idempotency-Key` per *attempt*, reused across that attempt's retries.
 *
 * React Query re-invokes `mutationFn` with the same variables object on a
 * retry, so keying off the object's identity is exactly the lifetime wanted: a
 * retry of one save reuses its key and the backend replays; a second save is a
 * new object and gets a new key. Generating the key inside `mutationFn` would
 * mint a fresh one per retry and make the whole mechanism decorative.
 */
const keys = new WeakMap();
function idempotencyKeyFor(payload) {
    const existing = keys.get(payload);
    if (existing)
        return existing;
    const key = crypto.randomUUID();
    keys.set(payload, key);
    return key;
}
function optimisticNook(payload) {
    return {
        id: crypto.randomUUID(),
        type: null,
        sourceUrl: payload.url ?? null,
        sourceTitle: payload.pageTitle ?? null,
        platform: null,
        sourceIcon: null,
        thumbnailUrl: null,
        // The bytes are already uploaded and could be shown from a local object
        // URL, but the row is replaced by the server's within a request and a
        // revoked URL in a stale cache is worse than a beat without a thumbnail.
        imageUrl: null,
        excerpt: payload.highlightText ?? null,
        // A note's body is its `user_note` (block 47).
        userNote: [payload.text, payload.note].filter(Boolean).join("\n\n") || null,
        summary: null,
        status: (payload.url ?? payload.storagePath) ? "processing" : "ready",
        capturedAt: new Date().toISOString(),
        editedAt: new Date().toISOString(),
        isNote: !(payload.url || payload.storagePath),
        highlightColor: payload.highlightText ? (payload.highlightColor ?? "yellow") : null,
        highlightAnchor: payload.highlightAnchor ?? null,
        optimistic: true,
    };
}
export function useCreateNook() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => getNookClient().createNook(payload, idempotencyKeyFor(payload)),
        // One retry, which is the reason the key above has to be stable. Without a
        // retry the idempotency contract is never exercised by the client that
        // depends on it; with more than one, a genuinely down backend keeps a modal
        // open for a quarter of a minute. An expired plan is not a blip (block 20).
        retry: (failures, error) => failures < 1 && !(error instanceof PlanExpiredError),
        async onMutate(payload) {
            await queryClient.cancelQueries({ queryKey: queryKeys.nooks.lists() });
            const snapshot = queryClient.getQueriesData({
                queryKey: queryKeys.nooks.lists(),
            });
            // The unfiltered list, and the Library's when this is not a note (block 19):
            // whether a filtered one would include it is the server's to derive, and
            // the invalidation below asks it.
            for (const key of payload.url || payload.storagePath
                ? [queryKeys.nooks.list(), queryKeys.nooks.list({ kind: NookKind.Capture })]
                : [queryKeys.nooks.list()])
                queryClient.setQueryData(key, (current) => current?.pages[0]
                    ? {
                        ...current,
                        pages: [
                            {
                                ...current.pages[0],
                                items: [optimisticNook(payload), ...current.pages[0].items],
                            },
                            ...current.pages.slice(1),
                        ],
                    }
                    : current);
            return { snapshot };
        },
        onError(_error, _payload, context) {
            for (const [key, data] of context?.snapshot ?? []) {
                queryClient.setQueryData(key, data);
            }
        },
        // On both paths: the optimistic row is a guess about ordering and about
        // what the server derived, and the authoritative answer is one fetch away.
        onSettled() {
            void queryClient.invalidateQueries({ queryKey: queryKeys.nooks.lists() });
            void queryClient.invalidateQueries({ queryKey: queryKeys.nooks.sites() });
        },
    });
}
//# sourceMappingURL=use-create-nook.js.map